import React, { useState ,useEffect } from 'react';
import { ChevronRight, ChevronLeft, Eye } from 'lucide-react';
import { Movement } from '../../types/movement';
import { CountdownTimer } from './CountdownTimer';
import { MovementTypeBadge } from './badges/MovementTypeBadge';
import { LocationBadge } from './badges/LocationBadge';
import { PilgrimCountBadge } from './badges/PilgrimCountBadge';
import { MovementFilters } from './filters/MovementFilters';
import { MovementDetailsModal } from './MovementDetailsModal';
import { usePilgrimsStore } from '../../stores/pilgrimsStore';
import { usePackagesStore } from '../../stores/packagesStore';
import { useFlightsStore } from '../../stores/flightsStore';
import { getMovementPilgrims } from '../../services/movements/pilgrimMatching';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  movements: Movement[];
  filters: any;
  onFilterChange: (key: string, value: any) => void;
  onClearFilters: () => void;
};

const ITEMS_PER_PAGE = 10;

export const MovementScheduleTable = ({ 
  movements = [],
  filters,
  onFilterChange,
  onClearFilters
}: Props) => {
  const { translations } = useLanguage();
  const [selectedMovement, setSelectedMovement] = useState<Movement | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { pilgrims } = usePilgrimsStore();
  const { packages } = usePackagesStore();
  const { flights } = useFlightsStore();

    // Reset to first page whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Calculate pagination
  const totalPages = Math.ceil(movements.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentData = movements.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <MovementFilters
        filters={filters}
        onFilterChange={onFilterChange}
        onClearFilters={onClearFilters}
        filteredMovements={movements}
      />

      {/* Table Container */}
      <div className="bg-white rounded-lg shadow-sm relative">
        {/* Table Content */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementTable?.headers?.type || 'Movement Type'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementTable?.headers?.from || 'From'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementTable?.headers?.to || 'To'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementTable?.headers?.date || 'Date'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementTable?.headers?.time || 'Time'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementTable?.headers?.countdown || 'Countdown'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementTable?.headers?.pilgrims || 'Pilgrims'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                  {translations?.movementTable?.headers?.actions || 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentData.map((movement) => (
                <tr key={movement.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <MovementTypeBadge type={movement.type} />
                  </td>
                  <td className="px-6 py-4">
                    <LocationBadge location={movement.from} />
                  </td>
                  <td className="px-6 py-4">
                    <LocationBadge location={movement.to} />
                  </td>
                  <td className="px-6 py-4">{movement.departureDate}</td>
                  <td className="px-6 py-4">{movement.departureTime}</td>
                  <td className="px-6 py-4">
                    <CountdownTimer 
                      targetDate={movement.departureDate} 
                      targetTime={movement.departureTime} 
                    />
                  </td>
                  <td className="px-6 py-4">
                    <PilgrimCountBadge count={movement.pilgrimsCount} />
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => setSelectedMovement(movement)}
                      className="p-2 text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                      title={translations?.movementTable?.actions?.viewDetails || 'View Details'}
                    >
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`p-1 rounded-lg ${
                currentPage === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronRight size={20} />
            </button>

            <span className="text-sm text-gray-700">
              {translations?.common?.pagination?.page || 'Page'} {currentPage} {translations?.common?.pagination?.of || 'of'} {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`p-1 rounded-lg ${
                currentPage === totalPages
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          <div className="text-sm text-gray-500">
            {movements.length} {translations?.movementTable?.movementCount || 'movements'}
          </div>
        </div>
      </div>

      {/* Movement Details Modal */}
      {selectedMovement && (
        <MovementDetailsModal
          isOpen={true}
          onClose={() => setSelectedMovement(null)}
          movement={selectedMovement}
          pilgrims={getMovementPilgrims(selectedMovement, pilgrims, packages, flights)}
        />
      )}
    </div>
  );
};
